import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../ui/dialog";
import { Library, Search, Sparkles, Trash2, Loader2, ImageIcon, Check } from "lucide-react";

/**
 * CampaignImageLibrary — reusable wellness image gallery.
 *
 * Workflow:
 *   1. AI-generated and uploaded campaign images are auto-saved to a
 *      Mongo-backed library (`engagement_images`).
 *   2. Admin opens this modal from the picker → searches/filters by tag.
 *   3. Click an image → onSelect(url) fires + library increments
 *      used_count so the most-reused images bubble up later.
 *
 * Props:
 *   open       — boolean
 *   onClose    — () => void
 *   onSelect   — (url) => void
 *   testid     — prefix for [data-testid]
 */
export default function CampaignImageLibrary({ open, onClose, onSelect, testid = "image-library" }) {
  const [items, setItems] = useState([]);
  const [tags, setTags] = useState([]);
  const [q, setQ] = useState("");
  const [activeTag, setActiveTag] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [reusing, setReusing] = useState(null);

  // Debounced query
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const tm = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(tm);
  }, [q]);

  const fetchTags = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/engagement/images/tags");
      setTags(data?.tags || []);
    } catch {
      setTags([]);
    }
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedQ) params.set("q", debouncedQ);
      if (activeTag) params.set("tag", activeTag);
      if (typeFilter) params.set("image_type", typeFilter);
      params.set("limit", "60");
      const { data } = await api.get(`/admin/engagement/images?${params.toString()}`);
      setItems(data?.items || []);
      setTotal(data?.total || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load library");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, activeTag, typeFilter]);

  useEffect(() => {
    if (open) {
      fetchTags();
      fetchItems();
    }
  }, [open, fetchTags, fetchItems]);

  const handlePick = async (img) => {
    if (!img?.url) return;
    setReusing(img.id);
    try {
      // Fire-and-forget the reuse counter — never block the selection.
      api.post(`/admin/engagement/images/${img.id}/reuse`).catch(() => {});
      onSelect?.(img.url);
      onClose?.();
    } finally {
      setReusing(null);
    }
  };

  const handleDelete = async (img, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete this image from the library?\n\n${(img.theme || img.url).slice(0, 80)}`)) return;
    try {
      await api.delete(`/admin/engagement/images/${img.id}`);
      toast.success("Image removed from library");
      setItems((prev) => prev.filter((x) => x.id !== img.id));
      setTotal((n) => Math.max(0, n - 1));
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || "Delete failed");
    }
  };

  const visibleTags = useMemo(() => tags.slice(0, 18), [tags]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid={`${testid}-dialog`}>
        <DialogHeader>
          <DialogTitle>
            <Library className="w-4 h-4 inline text-emerald-700 mr-1" /> Wellness image library
          </DialogTitle>
          <DialogDescription>
            Reuse images you've already generated or uploaded. Filter by tag or theme to find the right shot faster.
          </DialogDescription>
        </DialogHeader>

        {/* Search + type filter */}
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 text-emerald-700/50 absolute top-1/2 -translate-y-1/2 left-3" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by theme, topic, tag…"
              className="rounded-full pl-8"
              data-testid={`${testid}-search`}
            />
          </div>
          <div className="flex gap-1">
            <Button
              type="button"
              size="sm"
              variant={typeFilter === "" ? "default" : "outline"}
              onClick={() => setTypeFilter("")}
              className="rounded-full"
              data-testid={`${testid}-type-all`}
            >
              All
            </Button>
            <Button
              type="button"
              size="sm"
              variant={typeFilter === "ai_generated" ? "default" : "outline"}
              onClick={() => setTypeFilter(typeFilter === "ai_generated" ? "" : "ai_generated")}
              className="rounded-full"
              data-testid={`${testid}-type-ai`}
            >
              <Sparkles className="w-3 h-3 mr-1 text-amber-500" /> AI
            </Button>
            <Button
              type="button"
              size="sm"
              variant={typeFilter === "uploaded" ? "default" : "outline"}
              onClick={() => setTypeFilter(typeFilter === "uploaded" ? "" : "uploaded")}
              className="rounded-full"
              data-testid={`${testid}-type-uploaded`}
            >
              Uploads
            </Button>
          </div>
        </div>

        {/* Tag chips */}
        {visibleTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-tags`}>
            <button
              type="button"
              onClick={() => setActiveTag("")}
              className={`px-3 py-1 rounded-full text-[11px] font-semibold transition ${
                activeTag === ""
                  ? "bg-emerald-700 text-white"
                  : "bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
              }`}
              data-testid={`${testid}-tag-all`}
            >
              All tags
            </button>
            {visibleTags.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setActiveTag(t === activeTag ? "" : t)}
                className={`px-3 py-1 rounded-full text-[11px] font-semibold transition ${
                  activeTag === t
                    ? "bg-emerald-700 text-white"
                    : "bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                }`}
                data-testid={`${testid}-tag-${t.replace(/\s+/g, "-")}`}
              >
                {t}
              </button>
            ))}
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-16 text-emerald-700/70" data-testid={`${testid}-loading`}>
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading library…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-emerald-900/55" data-testid={`${testid}-empty`}>
            <ImageIcon className="w-8 h-8 mx-auto mb-3 text-emerald-700/35" />
            <p className="text-sm font-medium">No images yet</p>
            <p className="text-xs mt-1">Generate a wellness image with AI and it'll appear here for reuse.</p>
          </div>
        ) : (
          <>
            <div className="text-[11px] text-emerald-900/55 mb-2">
              Showing {items.length} of {total}
            </div>
            <div
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
              data-testid={`${testid}-grid`}
            >
              {items.map((img) => (
                <div
                  key={img.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handlePick(img)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handlePick(img);
                    }
                  }}
                  className="group relative rounded-xl overflow-hidden border border-emerald-100 bg-emerald-50/40 hover:ring-2 hover:ring-emerald-500/60 hover:shadow-md transition text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
                  data-testid={`${testid}-item-${img.id}`}
                  aria-label={`Use image: ${img.theme || img.topic || "Untitled"}`}
                >
                  <div className="aspect-[16/10] bg-emerald-50/60 overflow-hidden">
                    <img
                      src={img.url}
                      alt={img.theme || "Wellness image"}
                      className="w-full h-full object-cover transition group-hover:scale-105"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                    />
                  </div>
                  <div className="px-2.5 py-2">
                    <div className="text-[11px] font-semibold text-emerald-950 truncate">
                      {img.theme || img.topic || "Untitled"}
                    </div>
                    <div className="flex flex-wrap items-center gap-1 mt-1">
                      {img.image_type === "ai_generated" && (
                        <span className="text-[9px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-50 rounded-full px-1.5 py-0.5 flex items-center gap-0.5">
                          <Sparkles className="w-2.5 h-2.5" /> AI
                        </span>
                      )}
                      {(img.tags || []).slice(0, 2).map((t) => (
                        <span
                          key={t}
                          className="text-[9px] uppercase tracking-wider font-medium text-emerald-700 bg-emerald-50 rounded-full px-1.5 py-0.5"
                        >
                          {t}
                        </span>
                      ))}
                      {img.used_count > 0 && (
                        <span className="text-[9px] text-emerald-700/60 ml-auto">
                          · used {img.used_count}×
                        </span>
                      )}
                    </div>
                  </div>
                  {reusing === img.id && (
                    <div className="absolute inset-0 bg-emerald-700/60 flex items-center justify-center text-white">
                      <Check className="w-6 h-6" />
                    </div>
                  )}
                  {/* Delete control — top-right, only visible on hover */}
                  <button
                    type="button"
                    onClick={(e) => handleDelete(img, e)}
                    aria-label="Delete image"
                    className="absolute top-1.5 right-1.5 rounded-full bg-white/90 hover:bg-white p-1 shadow opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition"
                    data-testid={`${testid}-delete-${img.id}`}
                  >
                    <Trash2 className="w-3 h-3 text-rose-700" />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid={`${testid}-close`}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
